// i18n: translated
/**
 * 通知ベルアイコン + NotificationDrawer トリガー
 *
 * ヘッダー右上に配置。未読数バッジ付き。
 * クリックで NotificationDrawer を開閉する。
 * SPEC 7.1.1 通知ドロワー準拠。
 */
import { useState, useCallback } from 'react';
import { useT } from '@/i18n';
import { shellNs } from '@/i18n/namespaces/shell';
import NotificationDrawer from '@/components/layout/NotificationDrawer';
import { useUnreadCount } from '@/hooks/useNotifications';
import { usePermission } from '@/hooks/usePermission';

/**
 * 通知ベルアイコン
 *
 * 未読数をリアルタイム取得し、ドロワーを開閉する。
 * SPEC 7.1.1 通知ドロワー準拠。
 */
export default function NotificationBell() {
  const { canRead } = usePermission();
  return canRead('dashboard') ? <AuthorizedNotificationBell /> : null;
}

function AuthorizedNotificationBell() {
  const t = useT(shellNs);
  const [isOpen, setIsOpen] = useState(false);
  const { unreadCount, refetch: refetchUnread, applyOptimisticUnread } = useUnreadCount();

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // QA-120: ドロワークローズ時に未読数を再取得 (既読マーク反映)
    refetchUnread();
  }, [refetchUnread]);

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        className="relative rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        aria-label={t('notifications.bell')}
      >
        {/* Heroicons: bell */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="h-5 w-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
          />
        </svg>

        {/* 未読バッジ */}
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 通知ドロワー */}
      <NotificationDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onUnreadChange={applyOptimisticUnread}
        onUnreadRefetch={refetchUnread}
        unreadCount={unreadCount}
      />
    </>
  );
}
